import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';

import { Box as MuiBox, Drawer as MuiDrawer, List, ListItem, Typography } from '@mui/material';
// @ts-expect-error - Vite SVG import typing issue
import { ReactComponent as Logo } from '../assets/mosaic_icon.svg';
import { selectIsDevMode } from 'selectors';
import useAppNavigation from 'hooks/useAppNavigation';
import { useAppContext } from 'containers/AppProvider';
import { getModuleIcon } from '../constants/moduleIcons';
import { isSidebarRouteActive } from '../utils/routeMatching';
import { RouteType, RouteChildType, isRouteCategory } from '../types/routes';
import SidebarMenuItem from './SidebarMenuItem';

/* ============================================================================
 * STYLED COMPONENTS - Reference Design
 * ========================================================================== */

const Drawer = styled(MuiDrawer)`
  border-right: 0;
  > div {
    border-right: 0;
    overflow-x: hidden;
    overflow-y: auto;
  }
`;

const Scrollbar = styled.div`
  background: ${(props) =>
    props.theme.palette.mode === 'dark' ? props.theme.sidebar.background : '#f8f9fa'};
  border-right: 1px solid
    ${(props) =>
      props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.08)'};
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
`;

const Brand = styled(ListItem)`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${(props) => props.theme.palette.text.primary};
  background-color: ${(props) =>
    props.theme.palette.mode === 'dark' ? props.theme.header.background : 'transparent'};
  min-height: 64px;
  padding: ${(props) => props.theme.spacing(3, 3)};
  margin-bottom: 0;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    background-color: ${(props) =>
      props.theme.palette.mode === 'dark' ? props.theme.header.background : 'transparent'};
    opacity: 0.85;
  }

  @keyframes spinBounce {
    0% {
      transform: perspective(600px) rotateY(0deg) scale(1);
      filter: drop-shadow(0 0 8px rgba(251, 140, 0, 0.4));
    }
    70% {
      transform: perspective(600px) rotateY(380deg) scale(1.1);
      filter: drop-shadow(0 0 16px rgba(251, 140, 0, 1));
    }
    85% {
      transform: perspective(600px) rotateY(355deg) scale(1.05);
      filter: drop-shadow(0 0 14px rgba(251, 140, 0, 0.9));
    }
    100% {
      transform: perspective(600px) rotateY(360deg) scale(1);
      filter: drop-shadow(0 0 8px rgba(251, 140, 0, 0.4));
    }
  }

  /* Respect user's motion preferences */
  @media (prefers-reduced-motion: reduce) {
    &:hover {
      opacity: 1;
    }
  }
`;

const BrandIcon = styled(Logo)<{ component?: any }>`
  margin-right: ${(props) => props.theme.spacing(2)};
  color: ${(props) => props.theme.sidebar.header.brand.color};
  fill: ${(props) => props.theme.sidebar.header.brand.color};
  width: 32px;
  height: 32px;
  display: inline-block;
  cursor: pointer;
  transition: filter 0.3s ease;

  /* Base glow effect - enhanced */
  filter: drop-shadow(0 0 12px rgba(251, 140, 0, 0.6)) drop-shadow(0 0 4px rgba(251, 140, 0, 0.3));

  /* Hover effect - spin animation only, maintain same glow */
  ${Brand}:hover & {
    animation: spinBounce 0.8s ease-in-out !important;
  }

  /* Respect user's motion preferences for accessibility (WCAG) */
  @media (prefers-reduced-motion: reduce) {
    ${Brand}:hover & {
      animation: none !important;
      /* Subtle scale effect as fallback for reduced motion users */
      transform: scale(1.05);
      transition: transform 0.2s ease;
    }
  }
`;

const SectionHeader = styled(Typography)`
  color: ${(props) =>
    props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)'};
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  padding: ${(props) => props.theme.spacing(3, 3, 1.5, 3)};
  margin-top: ${(props) => props.theme.spacing(2)};
`;

/* ============================================================================
 * COMPONENT
 * ========================================================================== */

type SidebarProps = {
  // Note: routes prop removed - component uses sidebarRoutes from useAppContext() instead
  slotProps: { paper: { style: { width: number } } };
  variant?: 'permanent' | 'persistent' | 'temporary';
  open?: boolean;
  onClose?: () => void;
};

const SidebarNew: React.FC<SidebarProps> = ({ ...rest }) => {
  const location = useLocation();
  const { navigateToHome } = useAppNavigation();
  const isDevMode = useSelector(selectIsDevMode);
  const { sidebarRoutes } = useAppContext();
  const [randomSpin, setRandomSpin] = React.useState(false);

  // Random logo animation - occasional spontaneous spins!
  React.useEffect(() => {
    // Respect user's motion preferences
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    let timeoutId: NodeJS.Timeout | null = null;

    const triggerRandomSpin = () => {
      const shouldSpin = Math.random() < 0.12; // 12% chance
      if (shouldSpin) {
        setRandomSpin(true);
        timeoutId = setTimeout(() => {
          setRandomSpin(false);
          timeoutId = null;
        }, 800);
      }
    };

    // Check every 10-18 seconds
    const interval = setInterval(triggerRandomSpin, 10000 + Math.random() * 8000);

    return () => {
      clearInterval(interval);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <Drawer variant="permanent" {...rest}>
      <Brand onClick={() => navigateToHome()}>
        <BrandIcon style={{ animation: randomSpin ? 'spinBounce 0.8s ease-in-out' : 'none' }} />
        <MuiBox
          sx={{
            ml: 1,
          }}
        >
          Galaxy
        </MuiBox>
      </Brand>

      <Scrollbar>
        <List disablePadding>
          {sidebarRoutes.map((route: RouteType, routeIndex: number) => {
            const shouldShow = route.prod || isDevMode;
            if (!shouldShow) return null;

            return (
              <React.Fragment key={routeIndex}>
                {/* Section Header - show category ID as header if it has children */}
                {isRouteCategory(route) ? <SectionHeader>{route.id}</SectionHeader> : null}

                {/* If category has children, render them as menu items */}
                {isRouteCategory(route)
                  ? route.children.map((item: RouteChildType, itemIndex: number) => {
                      const itemShouldShow = item.prod || isDevMode;
                      if (!itemShouldShow) return null;

                      const { CustomSidebarComponent, ...routeProps } = item;

                      // If route has custom sidebar component, render that
                      if (CustomSidebarComponent) {
                        return <CustomSidebarComponent key={itemIndex} {...routeProps} />;
                      }

                      // Get icon for this item
                      const itemIcon = item.icon || getModuleIcon(item.name, 'sidebar');

                      // Otherwise render standardized menu item
                      return (
                        <SidebarMenuItem
                          key={itemIndex}
                          name={item.name}
                          path={item.path}
                          icon={itemIcon}
                          badge={typeof item.badge === 'number' ? String(item.badge) : item.badge}
                          active={isSidebarRouteActive(item.path, location.pathname)}
                        />
                      );
                    })
                  : /* Route without children - render as direct menu item */
                    route.path && (
                      <SidebarMenuItem
                        name={route.id}
                        path={route.path}
                        icon={route.icon}
                        badge={typeof route.badge === 'number' ? String(route.badge) : route.badge}
                        active={isSidebarRouteActive(route.path, location.pathname)}
                      />
                    )}
              </React.Fragment>
            );
          })}
        </List>
      </Scrollbar>
    </Drawer>
  );
};

export default SidebarNew;
